import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Button, Card, TextField } from '@ths/ui';
import { useState } from 'react';

import {
  useSendBookingConfirmationEmailMutation,
  BookingQuery,
} from 'generated/graphql';
import { isValidEmail } from 'utilities/validators';

import { useHotelUnsavedChanges } from '../../../hotel/session';

interface SendConfirmationEmailProps {
  booking: BookingQuery['booking'];
}

const SendConfirmationEmail = ({ booking }: SendConfirmationEmailProps) => {
  const [email, setEmail] = useState('');
  useHotelUnsavedChanges(email.length > 0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [validationError, setValidationError] = useState('');

  const [sendConfirmationEmail, { loading }] =
    useSendBookingConfirmationEmailMutation({
      onCompleted: () => {
        setShowSuccess(true);
        setShowError(false);
        setEmail('');
        setValidationError('');
        // Hide success message after 5 seconds
        setTimeout(() => setShowSuccess(false), 5000);
      },
      onError: () => {
        setShowError(true);
        setShowSuccess(false);
        // Hide error message after 5 seconds
        setTimeout(() => setShowError(false), 5000);
      },
    });

  const handleSendEmail = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setValidationError('Please enter an email address');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    setValidationError('');

    sendConfirmationEmail({
      variables: {
        input: {
          recipient: trimmedEmail,
          bookingId: booking.id,
        },
      },
    });
  };

  return (
    <Card surface="bordered" className="p-4">
      <div className="space-y-3">
        <h3 className="text-base font-bold">Send Confirmation Email</h3>
        <p className="text-sm text-text-secondary">
          Send a copy to a current client user who can access this booking
        </p>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <TextField
              label="Recipient email"
              error={validationError}
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value);
                setValidationError('');
              }}
              placeholder="Enter email address"
              disabled={loading}
            />
          </div>
          <Button
            onClick={handleSendEmail}
            disabled={loading}
            className="whitespace-nowrap"
          >
            {loading ? 'Sending...' : 'Send'}
          </Button>
        </div>

        {showSuccess && (
          <div
            role="status"
            className="bg-success-bg flex items-center gap-2 rounded-md p-2 text-success-text"
          >
            <CheckCircleIcon className="size-4" />
            <span className="text-xs">
              Confirmation email sent successfully!
            </span>
          </div>
        )}

        {showError && (
          <div
            role="alert"
            className="bg-error-bg flex items-center gap-2 rounded-md p-2 text-error-text"
          >
            <XCircleIcon className="size-4" />
            <span className="text-xs">
              The email could not be sent. Check that the recipient is a current
              client user with access to this booking.
            </span>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SendConfirmationEmail;
